import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Menu, Bot, HelpCircle, Search,
  Table, ShoppingCart, Package, Users, FileText,
  DollarSign, Truck, BarChart2, Settings, Archive,
  BookOpen, Tag, Layers, Grid, Building2, Scale
} from 'lucide-react';
import { ThemePreset } from '../theme/presets';

interface OutletContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (val: boolean) => void;
  currentPalette: ThemePreset;
}

interface CardItem {
  id: number;
  nmmenu: string;
  nmtooltip?: string;
  nmmetodo?: string;
  color: string;
  icon: React.ReactNode;
}

// Paleta de cores para os cards
const CARD_COLORS = [
  '#1976d2', '#2e7d32', '#ed6c02', '#9c27b0',
  '#d32f2f', '#0288d1', '#388e3c', '#f57c00',
  '#7b1fa2', '#1565c0', '#00695c', '#558b2f',
  '#4527a0', '#c62828', '#ad1457', '#283593',
];

// Ícones por palavra-chave no nome
const resolveIcon = (nome: string): React.ReactNode => {
  const n = nome.toLowerCase();
  if (n.includes('mercado') || n.includes('cliente')) return <Building2 size={22} />;
  if (n.includes('região') || n.includes('regiao')) return <Layers size={22} />;
  if (n.includes('marca')) return <Tag size={22} />;
  if (n.includes('família') || n.includes('familia') || n.includes('linha')) return <Layers size={22} />;
  if (n.includes('coleção') || n.includes('colecao') || n.includes('tamanho')) return <Archive size={22} />;
  if (n.includes('cor')) return <Grid size={22} />;
  if (n.includes('unidade') || n.includes('medida')) return <Scale size={22} />;
  if (n.includes('produto') || n.includes('categ')) return <Package size={22} />;
  if (n.includes('document') || n.includes('financ')) return <FileText size={22} />;
  if (n.includes('estoque') || n.includes('local')) return <Archive size={22} />;
  if (n.includes('usuario') || n.includes('usuário')) return <Users size={22} />;
  if (n.includes('venda') || n.includes('pedido')) return <ShoppingCart size={22} />;
  if (n.includes('pagamento') || n.includes('prazo')) return <DollarSign size={22} />;
  if (n.includes('entidade') || n.includes('forne')) return <Truck size={22} />;
  if (n.includes('conta') || n.includes('contab')) return <BookOpen size={22} />;
  if (n.includes('relat') || n.includes('dash')) return <BarChart2 size={22} />;
  if (n.includes('config') || n.includes('sistem')) return <Settings size={22} />;
  return <Table size={22} />;
};

// Mock estático — espelho das Tabelas Básicas do popular_telas.py + ThorJava típico
const MOCK_CARDS: CardItem[] = [
  { id: 1,  nmmenu: 'Mercados',               nmtooltip: 'Cadastro de mercados de atuação',      nmmetodo: 'Mercados',             color: CARD_COLORS[0],  icon: resolveIcon('mercado') },
  { id: 2,  nmmenu: 'Regiões',                nmtooltip: 'Cadastro de regiões de venda',         nmmetodo: 'Regioes',              color: CARD_COLORS[1],  icon: resolveIcon('região') },
  { id: 3,  nmmenu: 'Marcas',                 nmtooltip: 'Cadastro de marcas de produto',        nmmetodo: 'Marcas',               color: CARD_COLORS[2],  icon: resolveIcon('marca') },
  { id: 4,  nmmenu: 'Famílias',               nmtooltip: 'Classificação de famílias de produtos',nmmetodo: 'Familias',             color: CARD_COLORS[3],  icon: resolveIcon('família') },
  { id: 5,  nmmenu: 'Linhas',                 nmtooltip: 'Linhas de produto',                    nmmetodo: 'Linhas',               color: CARD_COLORS[4],  icon: resolveIcon('linha') },
  { id: 6,  nmmenu: 'Coleções',               nmtooltip: 'Coleções de produtos',                 nmmetodo: 'Colecoes',             color: CARD_COLORS[5],  icon: resolveIcon('coleção') },
  { id: 7,  nmmenu: 'Tamanhos',               nmtooltip: 'Tabela de tamanhos',                   nmmetodo: 'Tamanhos',             color: CARD_COLORS[6],  icon: resolveIcon('tamanho') },
  { id: 8,  nmmenu: 'Cores',                  nmtooltip: 'Tabela de cores',                      nmmetodo: 'Cores',                color: CARD_COLORS[7],  icon: resolveIcon('cor') },
  { id: 9,  nmmenu: 'Unidades de Medida',     nmtooltip: 'Unidades de medida de produtos',       nmmetodo: 'Unidades',             color: CARD_COLORS[8],  icon: resolveIcon('unidade') },
  { id: 10, nmmenu: 'Categorias de Produtos', nmtooltip: 'Categorias e subcategorias de produto',nmmetodo: 'CategoriasProdutos',   color: CARD_COLORS[9],  icon: resolveIcon('categ') },
  { id: 11, nmmenu: 'Locais de Estoque',      nmtooltip: 'Locais e depósitos de estoque',        nmmetodo: 'LocaisEstoque',        color: CARD_COLORS[10], icon: resolveIcon('estoque') },
];

export function BasicasPage() {
  const { sidebarOpen, setSidebarOpen, currentPalette } = useOutletContext<OutletContextType>();
  const navigate = useNavigate();
  const [cards, setCards] = useState<CardItem[]>(MOCK_CARDS);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const primaryStyle = { backgroundColor: currentPalette.primary };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return; // modo preview: mantém mock

    setLoading(true);
    fetch('/api/thorjava/menu/arvore?ativo=true', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data: any[] | null) => {
        if (!data) return;
        // Pegar itens do nível 1 que NÃO são pastas (folhas = telas reais)
        const basicas: CardItem[] = data
          .filter((item: any) => item.infolder !== 'S' && item.inseparador !== 'S')
          .slice(0, 30)
          .map((item: any, i: number) => ({
            id: item.id,
            nmmenu: item.nmmenu,
            nmtooltip: item.nmtooltip || item.nmmenu,
            nmmetodo: item.nmmetodo,
            color: CARD_COLORS[i % CARD_COLORS.length],
            icon: resolveIcon(item.nmmenu),
          }));
        if (basicas.length > 0) setCards(basicas);
      })
      .catch(() => {/* mantém mock */})
      .finally(() => setLoading(false));
  }, []);

  const filtered = cards.filter((c) =>
    c.nmmenu.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-950">
      {/* SUB-HEADER */}
      <div className="h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-850 flex items-center justify-between px-4 shadow-sm shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="text-lg font-semibold text-slate-800 dark:text-slate-100">Tabelas Básicas</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="flex items-center gap-2 px-4 py-2 hover:opacity-90 text-white rounded-full shadow-md transition-opacity text-sm font-medium"
            style={primaryStyle}
          >
            <Bot className="w-4 h-4" />
            Assistente IA
          </button>
          <button className="p-2 text-amber-600 dark:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/20 rounded-full transition-colors" title="Ajuda">
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-auto p-6">
        {/* Barra de busca */}
        <div className="mb-6 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Buscar tabela..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
            />
          </div>
          <span className="text-sm text-slate-500 dark:text-slate-400">{filtered.length} tabelas</span>
        </div>

        {/* Grid de cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-pulse">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 dark:bg-slate-900 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((card) => (
              <button
                key={card.id}
                onClick={() => {
                  const routeMap: Record<string, string> = {
                    'Mercados': '/t/mercados',
                    'Regioes': '/t/regioes',
                    'Marcas': '/t/marcas',
                    'Familias': '/t/familias',
                    'Linhas': '/t/linhas',
                    'Colecoes': '/t/colecoes',
                    'Cores': '/t/cores',
                    'Unidades': '/t/unidades',
                    'Unids': '/t/unidades',
                    'unids': '/t/unidades',
                    'unidades': '/t/unidades',
                  };
                  const route = routeMap[card.nmmetodo || ''] || `/t/${(card.nmmetodo || '').toLowerCase()}`;
                  navigate(route);
                }}
                className="group flex items-center gap-4 p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all text-left"
                style={{ borderLeftColor: card.color, borderLeftWidth: 4 }}
              >
                {/* Ícone */}
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-lg shrink-0 text-white"
                  style={{ backgroundColor: card.color }}
                >
                  {card.icon}
                </div>
                {/* Texto */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 dark:text-slate-200 text-sm leading-snug line-clamp-2 group-hover:text-slate-900 dark:group-hover:text-white">
                    {card.nmmenu}
                  </div>
                  {card.nmtooltip && card.nmtooltip !== card.nmmenu && (
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 line-clamp-1">
                      {card.nmtooltip}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
